import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, MapPin, User, Calendar, Phone, Mail, AlertTriangle,
  Wand2, Save, X, History, DollarSign,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  getDispatchJob, updateDispatch, geocodeLead, checkDispatchConflict,
  suggestStaffForLead, SERVICE_DETAIL_FIELDS, DISPATCH_LANES, labOrderDownloadUrl,
} from "@/lib/api";
import OpsAnalysisPanel from "@/components/dispatch/OpsAnalysisPanel";
import DispatchChat from "@/components/field/DispatchChat";
import { useAiContext } from "@/lib/aiContext";

const LANE_LABEL = Object.fromEntries(DISPATCH_LANES.map((l) => [l.value, l.label]));

export default function DispatchJobDialog({ open, leadId, staff = [], onClose, onSaved, onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [edit, setEdit] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // Surface this job to the floating AI Assistant so any prompt is scoped
  // to this specific lead. Reset on close so page-level context takes over.
  const aiCtx = useAiContext();
  useEffect(() => {
    if (!open || !leadId) return;
    aiCtx.set({
      page: "dispatch", entity_type: "lead", entity_id: leadId,
      label: job?.request_id ? `Job ${job.request_id}` : "This dispatch job",
    });
    return () => {
      aiCtx.set({ page: "dispatch", entity_type: null, entity_id: null, label: "Dispatch Center" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, job?.request_id]);

  useEffect(() => {
    if (!open || !leadId) return;
    setLoading(true);
    setJob(null);
    setEdit({});
    setConflicts([]);
    setSuggestions([]);
    let cancelled = false;
    getDispatchJob(leadId)
      .then((j) => {
        if (cancelled) return;
        setJob(j);
        setEdit({
          dispatch_status: j.dispatch_status,
          assigned_staff_id: j.assigned_staff_id || "",
          appointment_at: j.appointment_at || "",
          service_details: { ...(j.service_details || {}) },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) { onSessionExpired?.(); return; }
        toast.error("Failed to load job.");
        onClose?.();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Intentionally NOT depending on onSessionExpired/onClose — those are
    // inline callbacks from the parent that change reference on every parent
    // re-render (e.g. every 10s poll), which used to re-fire this effect and
    // cause the dialog to flicker/reload while the admin was editing.
  }, [open, leadId]);

  // When staff or appointment changes, check conflicts (debounced)
  useEffect(() => {
    if (!job || !edit.assigned_staff_id || !edit.appointment_at) {
      setConflicts([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await checkDispatchConflict({
          staff_id: edit.assigned_staff_id,
          appointment_at: edit.appointment_at,
          exclude_lead: job.id,
          window_minutes: 60,
        });
        setConflicts(res?.conflicts || []);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [edit.assigned_staff_id, edit.appointment_at, job]);

  const primaryService = useMemo(() => (job?.services?.[0] || "general"), [job]);
  const serviceFields = SERVICE_DETAIL_FIELDS[primaryService] || SERVICE_DETAIL_FIELDS.general;

  if (!open) return null;

  const setDetail = (k, v) =>
    setEdit((e) => ({ ...e, service_details: { ...(e.service_details || {}), [k]: v } }));

  const handleGeocode = async () => {
    if (!job) return;
    setGeoBusy(true);
    try {
      const res = await geocodeLead(job.id);
      setJob((j) => ({ ...j, lat: res.lat, lng: res.lng }));
      toast.success(`Geocoded: ${res.display_name?.slice(0, 60) || "OK"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geocoding failed.");
    } finally {
      setGeoBusy(false);
    }
  };

  const handleSuggest = async () => {
    if (!job) return;
    try {
      const res = await suggestStaffForLead(job.id);
      setSuggestions(res?.suggestions || []);
      if (!res?.suggestions?.length) toast.info("No previous staff coordinates yet to suggest from.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute suggestions.");
    }
  };

  const handleSave = async () => {
    if (!job) return;
    setSaving(true);
    try {
      const staffObj = staff.find((s) => s.id === edit.assigned_staff_id);
      const name = staffObj?.name || edit.assigned_staff_name || (edit.assigned_staff_id ? job.assigned_staff_name : null);
      const body = {
        dispatch_status: edit.dispatch_status,
        assigned_staff_id: edit.assigned_staff_id || null,
        assigned_staff_name: name,
        appointment_at: edit.appointment_at || null,
        service_details: edit.service_details || {},
      };
      await updateDispatch(job.id, body);
      toast.success("Job updated.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {job?.request_id && (
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded"
                    style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
                    data-testid="job-request-id">
                {job.request_id}
              </span>
            )}
            <span>{job?.full_name || (loading ? "Loading…" : "Job")}</span>
            {job?.urgency && (
              <span
                className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded"
                style={{
                  background: job.urgency === "Immediate" ? "rgba(155,28,28,0.10)"
                    : job.urgency === "Same Day" ? "rgba(212,175,55,0.18)"
                    : job.urgency === "Priority" ? "rgba(11,37,69,0.10)" : "#F1F5F9",
                  color: job.urgency === "Immediate" ? "#9B1C1C"
                    : job.urgency === "Same Day" ? "#7A5E12"
                    : job.urgency === "Priority" ? "#0B2545" : "#475569",
                }}
              >
                {job.urgency}
              </span>
            )}
            {job?.pipeline_stage && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D" }}
                    data-testid="job-pipeline-stage">
                {job.pipeline_stage}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {job?.services?.join(", ") || "Dispatch job details, assignment, scheduling, and service-specific data."}
          </DialogDescription>
        </DialogHeader>

        {loading || !job ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
          </div>
        ) : (
          <div className="grid gap-5 py-2">
            {/* Contact + payment */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
              <Info icon={Phone} label="Phone" value={job.phone} testid="job-phone" />
              <Info icon={Mail} label="Email" value={job.email} testid="job-email" />
              <Info icon={DollarSign} label="Payment"
                value={`${job.payment_status?.replace(/_/g, " ") || "unpaid"}${job.amount_due ? ` · $${job.amount_due}` : ""}`}
                tone={job.payment_status === "paid" ? "green" : job.payment_status === "unpaid" ? "red" : "navy"}
                testid="job-payment" />
            </div>

            {/* Address + geocode */}
            <div>
              <Label className="text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>Service Address</Label>
              <div className="mt-1 flex gap-2 items-start">
                <div className="flex-1 rounded-lg border p-2.5 text-sm" style={{ borderColor: "#E2E8F0" }}
                     data-testid="job-address">
                  <div className="flex items-start gap-1.5" style={{ color: "#0B2545" }}>
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#94A3B8" }} />
                    <span>{job.service_address || <em style={{ color: "#94A3B8" }}>No address on file</em>}</span>
                  </div>
                  {typeof job.lat === "number" && typeof job.lng === "number" && (
                    <div className="text-[10px] mt-1 ml-5" style={{ color: "#64748B" }}>
                      📍 {job.lat.toFixed(5)}, {job.lng.toFixed(5)}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={handleGeocode}
                        disabled={!job.service_address || geoBusy}
                        data-testid="job-geocode">
                  {geoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
                  Geocode
                </Button>
              </div>
            </div>

            {/* Status + appointment + staff (the operational triplet) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Status" testid="job-status-wrap">
                <Select value={edit.dispatch_status}
                        onValueChange={(v) => setEdit((e) => ({ ...e, dispatch_status: v }))}>
                  <SelectTrigger data-testid="job-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISPATCH_LANES.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Appointment (ISO)" hint="YYYY-MM-DDTHH:MM" testid="job-appt-wrap">
                <Input
                  type="datetime-local"
                  value={(edit.appointment_at || "").slice(0, 16)}
                  onChange={(e) => setEdit((p) => ({ ...p, appointment_at: e.target.value || "" }))}
                  data-testid="job-appointment"
                />
              </Field>
              <Field label="Assigned Staff" testid="job-staff-wrap">
                <Select value={edit.assigned_staff_id || "__none__"}
                        onValueChange={(v) => setEdit((e) => ({ ...e, assigned_staff_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger data-testid="job-staff"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Unassigned —</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.workload ? `· ${s.workload} active` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Conflict warning */}
            {conflicts.length > 0 && (
              <div className="rounded-xl border p-3" style={{ background: "rgba(155,28,28,0.06)", borderColor: "rgba(155,28,28,0.4)" }}
                   data-testid="job-conflicts">
                <div className="flex items-center gap-1.5 mb-1">
                  <AlertTriangle className="h-4 w-4" style={{ color: "#9B1C1C" }} />
                  <span className="text-xs font-semibold" style={{ color: "#9B1C1C" }}>
                    Schedule conflict — this staff member has {conflicts.length} other job{conflicts.length === 1 ? "" : "s"} within ±60 min
                  </span>
                </div>
                <ul className="ml-5 mt-1 space-y-0.5">
                  {conflicts.slice(0, 4).map((c) => (
                    <li key={c.id} className="text-[11px]" style={{ color: "#7F1D1D" }}>
                      • {c.full_name} · {c.appointment_at}{c.service_address ? ` · ${c.service_address.slice(0, 40)}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Route optimization (closest staff suggestions) */}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSuggest} data-testid="job-suggest-staff">
                <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Suggest closest staff
              </Button>
              {suggestions.length > 0 && (
                <div className="text-[11px] flex flex-wrap gap-1.5" data-testid="job-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setEdit((e) => ({ ...e, assigned_staff_id: s.id }))}
                      className="px-2 py-1 rounded-full border transition-colors"
                      style={{
                        background: edit.assigned_staff_id === s.id ? "#0B2545" : "#FFFFFF",
                        color: edit.assigned_staff_id === s.id ? "#FFD774" : "#0B2545",
                        borderColor: edit.assigned_staff_id === s.id ? "#0B2545" : "#E2E8F0",
                      }}
                      data-testid={`suggest-${s.id}`}
                    >
                      {s.name} · {s.distance_km} km
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Service-specific fields */}
            <div className="rounded-xl border p-3" style={{ background: "rgba(11,37,69,0.03)", borderColor: "#E2E8F0" }}
                 data-testid="job-service-details">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-2" style={{ color: "#7A5E12" }}>
                {primaryService.replace(/_/g, " ")} — Service-specific details
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {serviceFields.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <Label className="text-xs" style={{ color: "#0B2545" }}>{f.label}</Label>
                    {f.type === "select" ? (
                      <Select
                        value={edit.service_details?.[f.key] || "__none__"}
                        onValueChange={(v) => setDetail(f.key, v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger data-testid={`sd-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {f.options.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.type === "textarea" ? (
                      <Textarea
                        rows={3}
                        value={edit.service_details?.[f.key] || ""}
                        onChange={(e) => setDetail(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`sd-${f.key}`}
                      />
                    ) : (
                      <Input
                        type={f.type || "text"}
                        value={edit.service_details?.[f.key] || ""}
                        onChange={(e) => setDetail(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`sd-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* AI Operations Analysis — pricing, supplies, staff, checklist */}
            <OpsAnalysisPanel
              leadId={job.id}
              onApplyStaff={(s) => {
                setEdit((e) => ({ ...e, assigned_staff_id: s.id, assigned_staff_name: s.name }));
                toast.success(`${s.name} queued for assignment — click Save to confirm.`);
              }}
              onApplyPrice={(p) => {
                toast.info(`Recommended price: $${p.toFixed(2)}. Set this in the Billing panel.`);
              }}
              onSessionExpired={onSessionExpired}
            />

            {/* Dispatch Chat — bidirectional messaging with the assigned field staff */}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: "#E2E8F0", background: "#FFF" }}
                 data-testid="dispatch-chat-block">
              <div className="px-3 py-2 border-b flex items-center justify-between"
                   style={{ borderColor: "#F1F5F9", background: "#F8FAFC" }}>
                <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "#0B2545" }}>
                  Dispatch Chat
                </div>
                <div className="text-[10px]" style={{ color: "#64748B" }}>
                  {job.assigned_staff_name ? `with ${job.assigned_staff_name}` : "No staff assigned"}
                </div>
              </div>
              <div className="h-[380px] relative">
                <DispatchChat leadId={job.id} senderRoleHint="dispatch" />
              </div>
            </div>
            {/* Customer-submitted intake (read-only summary) + lab order link */}
            {(job.service_intake && Object.keys(job.service_intake).length > 0) || job.lab_order_file_id ? (
              <div className="rounded-xl border p-3" style={{ background: "rgba(15,109,93,0.04)", borderColor: "#E2E8F0" }}
                   data-testid="job-customer-intake">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-2" style={{ color: "#0F6D5D" }}>
                  Customer-submitted intake
                </div>
                {job.lab_order_file_id && (
                  <a
                    href={labOrderDownloadUrl(job.id)}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border mb-2"
                    style={{ borderColor: "#0F6D5D", color: "#0F6D5D" }}
                    data-testid="job-lab-order-download"
                  >
                    <Wand2 className="h-3 w-3" /> Download lab order ({job.lab_order_filename || "file"})
                  </a>
                )}
                {Object.entries(job.service_intake || {}).map(([group, fields]) => (
                  <div key={group} className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#475569" }}>
                      {group}
                    </div>
                    <ul className="ml-1 mt-0.5 grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
                      {Object.entries(fields || {}).map(([k, v]) => (
                        <li key={k} className="text-[11px]" style={{ color: "#0B2545" }}>
                          <span style={{ color: "#64748B" }}>{k.replace(/_/g, " ")}:</span> {String(v)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Dispatch history */}
            {(job.dispatch_history || []).length > 0 && (
              <div data-testid="job-history">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1 flex items-center gap-1.5"
                     style={{ color: "#64748B" }}>
                  <History className="h-3 w-3" /> Status history
                </div>
                <ul className="space-y-1">
                  {[...job.dispatch_history].reverse().slice(0, 8).map((h, i) => (
                    <li key={i} className="text-[11px] flex items-start gap-2"
                        style={{ color: "#475569" }}>
                      <span className="inline-block min-w-[5.5rem] font-mono" style={{ color: "#94A3B8" }}>
                        {(h.ts || "").slice(0, 16).replace("T", " ")}
                      </span>
                      <span className="font-medium" style={{ color: "#0B2545" }}>{LANE_LABEL[h.status] || h.status}</span>
                      <span>· by {h.by || "admin"} ({h.channel || "admin"})</span>
                      {h.note && <span>· {h.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="job-cancel">
            <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}
                  style={{ background: "#0B2545", color: "#FFFFFF" }}
                  data-testid="job-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ icon: Icon, label, value, tone, testid }) => {
  const palette =
    tone === "green" ? { color: "#0F6D5D", bg: "rgba(15,109,93,0.10)" }
    : tone === "red" ? { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" }
    : { color: "#0B2545", bg: "rgba(11,37,69,0.06)" };
  return (
    <div className="rounded-lg border p-2.5 flex items-start gap-2"
         style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      <span className="grid place-items-center h-6 w-6 rounded-md shrink-0"
            style={{ background: palette.bg, color: palette.color }}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "#64748B" }}>{label}</div>
        <div className="text-xs truncate" style={{ color: "#0B2545" }}>{value || "—"}</div>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs uppercase tracking-wider" style={{ color: "#64748B" }}>
      {label}{hint && <span className="ml-1 normal-case font-normal" style={{ color: "#94A3B8" }}>· {hint}</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);
